import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentIdentity } from '../../../../api/enrollments/get-enrollment-identity'
import useTranslate from '../../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { IDTypeEnum } from '../../../../lib/types/enums/common/id-type.enum'
import { enrollmentClientIdentityBaseFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { enrollmentIdentityBaseFields } from '../../../../lib/types/fields/enrollments/enrollment-identity'
import { getCountryLabel } from '../../../../lib/types/models/country/country-list'
import CountryFlag from '../../../elements/country-flag'
import LoadingScreen from '../../../elements/loading/loading-screen'
import { LabelValue } from './shared-client-space-ui'

type ClientEnrollmentIdentityProps = {
  enrollmentId: number
}

export function ClientEnrollmentIdentity({ enrollmentId }: ClientEnrollmentIdentityProps) {
  const { currentLanguage } = useTranslate()
  const { data: enrollment, isPending: isEnrollmentPending } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentClientIdentityBaseFields],
    queryFn: () => getEnrollment(enrollmentId, enrollmentClientIdentityBaseFields),
    enabled: !!enrollmentId
  })

  const { data: enrollmentIdentity, isPending: isEnrollmentIdentityPending } = useQuery({
    queryKey: ['enrollment-identity', enrollmentId, enrollmentIdentityBaseFields],
    queryFn: () => getEnrollmentIdentity(enrollmentId, enrollmentIdentityBaseFields),
    enabled: !!enrollmentId
  })

  const isIdentityDocumentPassport =
    enrollmentIdentity?.identityDocumentType === IDTypeEnum.PASSPORT

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  if (!enrollment || isEnrollmentPending || isEnrollmentIdentityPending) return <LoadingScreen />

  return (
    <Box className={'border border-grey-light rounded-lg p-4 bg-white-hover'}>
      <Typography variant={'text'}>Informations d'identité de l'enfant</Typography>

      <div className={'mobile:grid-cols-1 grid grid-cols-2 gap-4 mt-3'}>
        <LabelValue label={'Nom'} color={tailwindColors.grey.lighter}>
          <Typography variant={'tag'}>
            {valueRender(enrollment.childLastName, (l) => l.toUpperCase())}
          </Typography>
        </LabelValue>

        <LabelValue label={'Prénom'} color={tailwindColors.grey.lighter}>
          <Typography variant={'tag'}>
            {enrollment.childFirstName
              ? StringFormatter.capitalize(enrollment.childFirstName)
              : ''}
          </Typography>
        </LabelValue>

        <LabelValue label={'Date de naissance'} color={tailwindColors.grey.lighter}>
          <Badge text={valueRender(enrollment.childBirthDate, (d) => DateFormatter.DDMMYYYY(d))} />
        </LabelValue>

        <LabelValue label={'Sexe'} color={tailwindColors.grey.lighter}>
          <Typography variant={'tag'}>
            {valueRender(enrollment.childGender, (g) => genderOneLetterMapping[g].label)}
          </Typography>
        </LabelValue>

        <LabelValue label={'Nationalité'} color={tailwindColors.grey.lighter}>
          <CountryFlag code={enrollment.childNationality} />
        </LabelValue>

        <LabelValue label={'Pays de résidence'} color={tailwindColors.grey.lighter}>
          <CountryFlag
            code={enrollment.residenceCountry}
            label={
              getCountryLabel(
                currentLanguage,
                countryList?.data.find((c) => c.key === enrollment.residenceCountry)
              ) || '-'
            }
          />
        </LabelValue>

        {enrollmentIdentity && (
          <>
            <LabelValue
              label={isIdentityDocumentPassport ? 'N° de passeport' : 'N° Document'}
              color={tailwindColors.grey.lighter}
            >
              <Typography variant={'tag'}>{enrollmentIdentity.identityDocumentNumber}</Typography>
            </LabelValue>

            <LabelValue label={'Date de délivrance'} color={tailwindColors.grey.lighter}>
              {enrollmentIdentity.identityDocumentIssuedAt && (
                <Badge
                  text={DateFormatter.DDMMYYYY(enrollmentIdentity.identityDocumentIssuedAt)}
                />
              )}
            </LabelValue>

            <LabelValue label={'Pays émetteur'} color={tailwindColors.grey.lighter}>
              {enrollmentIdentity.identityDocumentIssuingCountry ? (
                <CountryFlag
                  code={enrollmentIdentity.identityDocumentIssuingCountry}
                  label={
                    getCountryLabel(
                      currentLanguage,
                      countryList?.data.find(
                        (c) => c.key === enrollmentIdentity.identityDocumentIssuingCountry
                      )
                    ) || '-'
                  }
                />
              ) : (
                '-'
              )}
            </LabelValue>

            <LabelValue label={"Date d'expiration"} color={tailwindColors.grey.lighter}>
              {enrollmentIdentity.identityDocumentExpiration && (
                <Badge
                  text={DateFormatter.DDMMYYYY(enrollmentIdentity.identityDocumentExpiration)}
                />
              )}
            </LabelValue>
          </>
        )}
      </div>
    </Box>
  )
}
